import qs from 'qs';
import config from 'config';
import { axios, path, formAxios } from './config';
import { windowStore } from 'util/windowStore';
import { replaceSpecialChars, convertSort } from 'util/helpers';
import {
  cacheKey,
  pageNum,
  candidateStatusTypes,
  productTypes,
  CandidateSource,
  APP_NAME,
  statusTypes,
  FIVE_THOUSAND,
} from 'appConstants';
import { logoutAuth0 } from './auth0';

const { ONE, FIVE, TEN } = pageNum;

export function lookup({ username }) {
  return axios.post(`${config.lookupApiUrl}/users`, { username });
}

export function sso({ username }) {
  return axios.get(path.REMOTE_AUTH_INFO, { params: { username } });
}

export function sessionInfo() {
  return axios.get(path.LOGIN).then((response) => {
    if (!windowStore.auth0) {
      windowStore.set('lastRefreshedTime', new Date().getTime());
    }

    windowStore.set(cacheKey.USER_ID, response.userInfo.userName);
    if (window.newrelic && window.newrelic.setUserId) {
      window.newrelic.setUserId(response.userInfo.userName);
    }
    return response;
  });
}

export function login({ username, password, isRememberMe }) {
  let token = cacheKey.LL_TOKEN;
  // eslint-disable-next-line no-underscore-dangle
  windowStore._storage = 'localStorage';
  if (!isRememberMe) {
    // eslint-disable-next-line no-underscore-dangle
    windowStore._storage = 'sessionStorage';
    token = cacheKey.TOKEN;
  }
  const data = {
    username,
    password,
    remember_me: isRememberMe,
  };
  /* Here we got ?& in the URL because there are no extra params other than the default params in axios.create() */
  return axios.post(path.AUTHENTICATE, data).then((response) => {
    windowStore.set(token, response.token);
    return response;
  });
}

export function getWebSocketId() {
  return axios.get(path.WEBSOCKET_ID);
}

export function logout() {
  sessionStorage.removeItem('hasLoggedInTrackForPendo');
  if (windowStore.auth0) {
    windowStore.clear();
    if (window.newrelic && window.newrelic.setUserId) {
      window.newrelic.setUserId(null);
    }
    return logoutAuth0();
  }
  return axios.get(path.LOGOUT);
}

export function forgotPassword({ username }) {
  return axios.post(path.FORGOT_PASSWORD + qs.stringify({ username }));
}

export function verifyPassword({ id }) {
  return axios.post(path.VERIFY_PASSWORD + qs.stringify({ id }));
}

export function recoverPassword({ id, password }) {
  const data = {
    id,
    new_password_1: password,
    new_password_2: password,
  };
  return axios.post(path.RECOVER_PASSWORD, data);
}

export function availability() {
  return axios.get(path.AVAILIBILITY);
}

export function disconnectCalendar({ type }) {
  return axios.get(path.DISCONNECT_CALENDAR + type);
}

export function changePassword({ username, password, newPassword }) {
  const params = {
    username,
    password,
    new_password_1: newPassword,
    new_password_2: newPassword,
  };
  return axios.post(path.CHANGE_PASSWORD, params);
}

export function fetchTokenInfo() {
  const accountId = axios.defaults.params?.account_id;
  const body = {
    token_request: true,
    account_id: accountId,
    customer_id: accountId,
  };
  return axios.post(path.TOKEN_INFO, body, {
    cache: false,
  });
}

export function addCandidate({ firstName, lastName, phoneNumber, channel, jobTitle }) {
  const params = {
    first_name: firstName,
    last_name: lastName,
    phone: phoneNumber,
    channel,
    job_title: jobTitle ? jobTitle : '',
  };

  return axios.post(`${path.ADD_CANDIDATE_API}${qs.stringify(params)}`);
}

export function addUserApi({ firstName, lastName, desiredCode, emailAddress, role, isChecked }) {
  const params = {
    first_name: firstName,
    last_name: lastName,
    areaCode: desiredCode,
    email_address: emailAddress,
    new_username: emailAddress,
    role,
    send_invite: isChecked,
  };

  return axios.post(`${path.MGR_USER_CREATE}${qs.stringify(params)}`);
}

export function updateUserApi({ firstName, lastName, id }) {
  const params = {
    first_name: firstName,
    last_name: lastName,
    id,
  };

  return axios.post(`${path.MGR_USER_UPDATE}${qs.stringify(params)}`);
}

export function addNewCampaign({ name, url, code, autoResponse, sequence = {} }) {
  const { sequenceDefinitionId = '', displayName = '' } = sequence;

  const params = {
    name,
    url,
    code,
    outbound_ari_name: displayName,
    sequence_id: sequenceDefinitionId,
    auto_response: autoResponse,
  };

  return axios.post(`${path.ADD_CAMPAIGN}${qs.stringify(params)}`);
}

export function starCandidateApi({ candidateId, isStarred }) {
  const pathName = isStarred ? path.REMOVE_CANDIDATE_TAG : path.ADD_CANDIDATE_TAG;
  const params = {
    candidate_id: candidateId,
    tag_name: candidateStatusTypes.STARRED,
  };

  return axios.post(`${pathName}${qs.stringify(params)}`);
}
export function inactiveCandidateApi({ candidateId, isInactive }) {
  const statusUpdate = isInactive ? candidateStatusTypes.ACTIVE : candidateStatusTypes.INACTIVE;
  const params = {
    candidate_id: candidateId,
    status: statusUpdate,
  };
  return axios.post(`${path.UPDATE_CANDIDATE}${qs.stringify(params)}`);
}

export function archiveCandidateApi({ candidateId, isArchive }) {
  const statusChange = isArchive ? candidateStatusTypes.ACTIVE : candidateStatusTypes.DELETED;
  const params = {
    candidate_id: candidateId,
    status: statusChange,
  };

  return axios.post(`${path.UPDATE_CANDIDATE}${qs.stringify(params)}`);
}

export function removeSelectedCampaignsApi({ candidateId, jobIds }) {
  const params = {
    candidate_id: candidateId,
    job_ids: jobIds.join(','),
  };

  return axios.post(`${path.REMOVE_CAMPAIGNS_FROM_CANDIDATE}${qs.stringify(params)}`);
}

export function updateAllActiveCandidateApi({ candidateId }) {
  const params = {
    candidate_id: candidateId,
    status: candidateStatusTypes.ACTIVE,
  };

  return axios.post(`${path.UPDATE_CANDIDATE}${qs.stringify(params)}`);
}
export function updateDoNotTextCandidateApi({ candidateId }) {
  const params = {
    candidate_id: candidateId,
  };

  return axios.post(`${path.DO_NOT_TEXT}${qs.stringify(params)}`);
}
export function fetchCandidateByPhone({ phone }) {
  return axios.get(path.GET_CANDIDATE_BY_PHONE, { params: { phone } });
}

export function fetchOrAdd({ firstName, lastName, phoneNumber, channel }) {
  return fetchCandidateByPhone({ phone: phoneNumber })
    .then((response) => response)
    .catch(() => addCandidate({ firstName, lastName, phoneNumber, channel }));
}

export function searchCandidates({ pageNumber = ONE, value = '' }) {
  const pureValue = replaceSpecialChars(value);
  const isNumber = !isNaN(pureValue);
  const searchValue = isNumber ? pureValue : value;
  const fieldName = isNumber ? 'phoneNumber' : 'fullName';

  const requestBody = {
    items_per_page: 15,
    page: pageNumber,
    sort: '-created',
    nocount: true,
    filter: [
      { fieldName, type: 'Like', value: searchValue },
      { fieldName: 'restricted', type: 'NotEquals', value: true },
    ],
  };

  return axios.post(path.CANDIDATES, requestBody);
}

export function getCandidates({
  pageNumber = ONE,
  itemsPerPage = TEN,
  sort,
  tagId = null,
  filter,
  status = null,
}) {
  const requestBody = {
    items_per_page: itemsPerPage,
    page: pageNumber,
    sort,
    tag_id: tagId,
    filter,
    status,
  };

  if (tagId) {
    filter.push({ fieldName: 'fullTags.id', type: 'AllLike', value: tagId });
  }
  return axios.post(path.CANDIDATES, requestBody);
}

export function getATSCandidatesAPI({ itemsPerPage, pageNumber, sort, searchValue }) {
  const requestBody = {
    items_per_page: itemsPerPage,
    page: pageNumber,
    sort,
    source: CandidateSource.ICIMS,
  };

  if (searchValue) {
    requestBody.filter = JSON.stringify([
      { fieldName: 'firstName', type: 'Like', value: searchValue },
    ]);
  }

  return axios.post(`${path.CANDIDATES}`, requestBody);
}

export function getaddATSCandidate({ candidatesIds }) {
  const params = {
    source: CandidateSource.ICIMS,
  };
  return axios.post(
    `${path.ADD_ATS_CANDIDATE}candidate_ids=${candidatesIds}&${qs.stringify(params)}`,
  );
}

export function purgeCandidates({ candidatesIds }) {
  return axios.post(`${path.DELETE_CANDIDATES}candidate_ids=${candidatesIds}`);
}

export function getTagsOptions({
  pageNumber = ONE,
  itemsPerPage = TEN,
  sort = '-id',
  searchValue,
}) {
  const params = {
    items_per_page: itemsPerPage,
    page: pageNumber,
    sort,
    filter: JSON.stringify([{ fieldName: 'name', type: 'Like', value: searchValue }]),
  };
  return axios.get(`${path.TAGS}${qs.stringify(params)}`);
}

export function getStarredCandidates({ pageNumber = ONE, itemsPerPage = TEN, sort, filter = [] }) {
  const queryFilter = {
    fieldName: 'fullTags.name',
    type: 'AllLike',
    value: 'Starred',
  };
  filter.push(queryFilter);
  const requestBody = {
    filter,
    items_per_page: itemsPerPage,
    page: pageNumber,
    sort,
  };

  return axios.post(`${path.CANDIDATES}`, requestBody);
}

export function fetchCandidateById({ id }) {
  return axios.post(path.GET_CANDIDATE_BY_ID, { candidate_id: id });
}

export function getCandidatesCampaignById({ candidateId }) {
  return axios.post(path.GET_CANDIDATE_BY_ID, { candidate_id: candidateId });
}

export function getCandidatesMetricsById({ candidateId }) {
  const params = { candidate_id: candidateId };
  return axios.get(path.CANDIDATE_METRICS_BY_ID, { params });
}

export function removeCandidateTag({ candidateId, tagName }) {
  const params = {
    candidate_id: candidateId,
    tag_name: tagName,
  };

  return axios.post(`${path.REMOVE_CANDIDATE_TAG}${qs.stringify(params)}`);
}

export function addCandidateTag({ candidateId, tagName }) {
  const params = {
    candidate_id: candidateId,
    tag_name: tagName,
  };

  return axios.post(`${path.ADD_CANDIDATE_TAG}${qs.stringify(params)}`);
}

export function addATSCandidateToCampaignApi({ campaignId, candidateId, customerId }) {
  const data = {
    job_id: campaignId,
    candidate_ids: candidateId,
    account_id: customerId,
    app_name: APP_NAME,
    product: productTypes.TEXT_RECRUIT,
    client_ver: config.clientVersion,
    source: CandidateSource.ICIMS,
  };

  return formAxios.post(path.ADD_CANDIDATE_TO_CAMPAIGN, qs.stringify(data));
}

export function addCandidateToCampaign({ campaignId, candidateId }) {
  return axios.post(
    path.ADD_CANDIDATE_TO_CAMPAIGN +
      qs.stringify({ job_id: campaignId, candidate_ids: candidateId }),
  );
}

export function addBulkCandidateToCampaign({ campaignId, filter }) {
  const formattedFilter = [{ fieldName: 'channel', type: 'NotEquals', value: 'WhatsApp' }];

  const filterMappings = [
    { key: 'fullName', fieldName: 'fullName', type: 'Like' },
    { key: 'tags', fieldName: 'fullTags.id', type: 'AllLike', isMultiple: true },
    { key: 'phoneNumber', fieldName: 'phoneNumber', type: 'Like' },
  ];

  for (const { key, fieldName, type, isMultiple } of filterMappings) {
    const field = filter[key];
    if (!field) continue;

    const values = isMultiple ? field.map(({ id }) => id) : [field.value];

    values.forEach((value) => {
      formattedFilter.push({ fieldName, type, value });
    });
  }

  return axios.post(
    path.BULK_CANDIDATE_ADD +
      qs.stringify({ job_id: campaignId, filter: JSON.stringify(formattedFilter) }),
  );
}

export function addCandidateToTag({ tagId, candidateId }) {
  return axios.post(
    path.ADD_CANDIDATE_TO_TAG + qs.stringify({ tag_id: tagId, candidate_ids: candidateId }),
  );
}

export function validateCandidate({ candidateId }) {
  return axios.get(path.VALIDATE_CANDIDATE, {
    params: { candidate_id: candidateId, phone_validation_level: 17 },
  });
}

export function getCampaign({ jobId }) {
  return axios.get(path.CAMPAIGN_API, {
    params: { job_id: jobId },
    /**
     * TODO: forceUpdate ensures this API resp. is not cached.
     *
     * It's because response.candidates data will not be updated
     * if the same campaign has been added to a other candidate(s)
     * within the last 5 mins.
     *
     * Ideally we can cache it and update response.candidates
     * when the same campaign has been added to other candidate(s).
     * The solution is a little complicated and for now we can
     * punt on caching. -Amir
     */
    forceUpdate: true,
  });
}

export function getAtsUser({ atsUserId, email }) {
  const params = {};
  if (atsUserId) {
    params.icims_user_id = atsUserId;
  }
  if (email) {
    params.email_address = email;
  }

  return axios.get(path.ATS_USER, {
    params,
    forceUpdate: true,
  });
}

export function ingestAtsUser({ atsUserId, email }) {
  const params = {};
  if (atsUserId) {
    params.icims_user_id = atsUserId;
  }
  if (email) {
    params.email_address = email;
  }

  return axios.post(`${path.INGEST_ATS_USER}${qs.stringify(params)}`, {
    forceUpdate: true,
  });
}

export function searchCampaigns({ value, itemsPerPage, sort, filter, page, isAutoSuggest }) {
  const formattedFilter = JSON.stringify([
    { fieldName: 'type', type: value, value: 'REENGAGEMENT' },
    { fieldName: 'status', type: 'Equals', value: 'open' },
    ...filter,
  ]);

  const formattedAutoSuggestFilter = JSON.stringify([...filter]);

  const params = {
    items_per_page: itemsPerPage || TEN,
    sort,
    filter: isAutoSuggest ? formattedAutoSuggestFilter : formattedFilter,
    page,
  };

  return axios.get(`${path.CAMPAIGNS_API}${qs.stringify(params)}`);
}

export function getMessageTemplates({ itemsPerPage = FIVE_THOUSAND, shareFilter, code, sort }) {
  return axios.get(path.MESSAGE_TEMPLATES, {
    params: {
      items_per_page: itemsPerPage,
      share_filter: shareFilter,
      code,
      sort,
    },
  });
}

export function getICIMSCandidateData({ candidateId }) {
  return axios.get(path.INTEGRATION_DATA, {
    params: { candidate_id: candidateId, source: CandidateSource.ICIMS },
  });
}

export function validatePhoneNumber({ phone }) {
  return axios.get(path.VALIDATE_PHONE_NUMBER, { params: { phone } });
}

export function updateCandidateChannel({ candidateId, channel }) {
  const params = { candidate_id: candidateId, channel };

  return axios.post(`${path.UPDATE_CANDIDATE}${qs.stringify(params)}`);
}

export function updateCandidate({
  candidateId,
  firstName,
  lastName,
  phoneNumber,
  channel,
  jobTitle,
}) {
  const params = {
    candidate_id: candidateId,
    first_name: firstName,
    last_name: lastName,
    phone: phoneNumber,
    channel,
    job_title: jobTitle,
  };

  return axios.post(`${path.UPDATE_CANDIDATE}${qs.stringify(params)}`);
}

export function updateUserPreferences({ blacklistedDomains, preferredPhoneNumberRegions }) {
  const params = {};
  if (preferredPhoneNumberRegions) {
    params.preferred_phone_regions = JSON.stringify(preferredPhoneNumberRegions);
  }
  if (blacklistedDomains) {
    params.blacklisted_domains = JSON.stringify(blacklistedDomains);
  }
  return axios.post(`${path.UPDATE_USER_PREFERENCES}${qs.stringify(params)}`);
}

export function mobileSignup({ email, password, fullName, company, phone, contactMe }) {
  const params = {
    email,
    password,
    full_name: fullName,
    company,
    phone,
    contact_me: contactMe,
  };

  return axios.get(path.ADD_MOBILE_ACCOUNT, { params });
}

export function addPushDevice({ deviceId, deviceToken }) {
  const params = { device_id: deviceId, device_token: deviceToken };

  return axios.get(path.ADD_PUSH_DEVICE, { params });
}

export function removePushDevice({ deviceId, deviceToken }) {
  const params = { device_id: deviceId, device_token: deviceToken };

  return axios.get(path.REMOVE_PUSH_DEVICE, { params });
}

export function updateAvailibility({ calendarAvailability }) {
  const params = {
    calendar_availability: JSON.stringify(calendarAvailability),
  };

  return axios.post(`${path.AVAILIBILITY_UPDATE}${qs.stringify(params)}`);
}

export function updateBookingSettings({ bookingDelay, bookingDuration, delayBookingToNextDay }) {
  const params = {
    booking_delay: bookingDelay,
    booking_duration: bookingDuration,
    delay_booking_to_next_day: delayBookingToNextDay,
  };

  return axios.post(`${path.UPDATE_BOOKING_SETTINGS}${qs.stringify(params)}`);
}

export function fetchUsersApi({ page, itemsPerPage = TEN, sort, filter, phone }) {
  const queryFilter = [{ fieldName: 'status', type: 'NotEquals', value: 'deleted' }];

  let formattedFilter = null;

  if (filter) {
    formattedFilter = JSON.stringify([...queryFilter, ...filter]);
  } else {
    formattedFilter = JSON.stringify(queryFilter);
  }

  const params = {
    filter: formattedFilter,
    items_per_page: itemsPerPage,
    page,
    sort,
    ...(phone && { phone }),
  };

  return axios.get(path.MGR_USERS, {
    params,
  });
}

export function fetchUsersOmni({ params }) {
  params.has_license = true;
  return axios.get(path.MGR_USERS_OMNI, { params });
}

export function fetchOmniSearch({ search }) {
  return axios.get(path.OMNI_SEARCH, { params: { search, mode: 'secret' } });
}

export function fetchUsersEx(filters) {
  const queryFilter = [];

  queryFilter.push({
    fieldName: 'status',
    type: 'NotEquals',
    value: 'deleted',
  });
  if (filters.lastName) {
    queryFilter.push({
      fieldName: 'last_name',
      type: 'Like',
      value: filters.lastName,
    });
  }
  if (filters.userName) {
    queryFilter.push({
      fieldName: 'userName',
      type: 'Like',
      value: filters.userName,
    });
  }
  if (filters.exactUserName) {
    queryFilter.push({
      fieldName: 'userName',
      type: 'Equals',
      value: filters.exactUserName,
    });
  }

  const params = {
    filter: JSON.stringify(queryFilter),
    items_per_page: filters.itemsPerPage || TEN,
    page: filters.page,
    sort: convertSort({ filters }),
  };

  if (filters.phoneNumber) {
    params.phone = filters.phoneNumber;
  }

  return axios.get(path.MGR_USERS, { params });
}

export function sendAsUser(sendAs) {
  const params = {
    send_as: sendAs,
  };

  return axios.get(path.USER_SEND_AS, { params });
}

export function user(userId) {
  const params = {
    id: userId,
  };

  return axios.get(path.MGR_USER, { params });
}

export function createUser({ userName, role, areaCode, firstName, lastName, sendInvite }) {
  const params = {
    new_username: userName,
    role,
    first_name: firstName,
    last_name: lastName,
  };
  if (sendInvite) {
    params.send_invite = 'true';
  } else {
    params.send_invite = 'false';
  }
  if (areaCode) {
    params.areacode = areaCode;
  }

  return axios.post(path.MGR_USER_CREATE, { params });
}

export function updateUserMgr({ userId, firstName, lastName, role }) {
  const params = {
    id: userId,
    first_name: firstName,
    last_name: lastName,
    role,
  };

  return axios.post(path.MGR_USER_UPDATE, { params });
}

export function revokeUser({ userId, role }) {
  const params = {
    id: userId,
    role,
  };

  return axios.post(path.MGR_USER_REVOKE, { params });
}

export function addICIMSUserId({ userId, icimsUserId }) {
  const params = {
    id: userId,
    userId: icimsUserId,
  };

  return formAxios.post(`${path.MGR_USER_INT_ICIMS_ADD_USER_ID}${qs.stringify(params)}`);
}

export function removeICIMSUserId({ userId, icimsUserId }) {
  const params = {
    id: userId,
    userId: icimsUserId,
  };

  return formAxios.post(`${path.MGR_USER_INT_ICIMS_REMOVE_USER_ID}${qs.stringify(params)}`);
}

export function removeLicense({ userId }) {
  return axios.post(path.MGR_USER_LICENSE_REMOVE + qs.stringify({ id: userId }));
}

export function addLicense({ userId, areaCode }) {
  return axios.post(path.MGR_USER_LICENSE_ADD + qs.stringify({ id: userId, areacode: areaCode }));
}

export function expirePassword({ userId }) {
  return axios.post(path.MGR_USER_EXPIRE_PASSWORD + qs.stringify({ id: userId }));
}

export function addUserInbox({ userId, userName, inboxName }) {
  const params = {
    id: userId,
    username: userName,
    name: inboxName,
  };
  return axios.post(`${path.MGR_USER_ADD_INBOX}${qs.stringify(params)}`);
}

export function removeUserInbox({ userId, userName }) {
  const params = {
    id: userId,
    username: userName,
  };
  return axios.post(`${path.MGR_USER_REMOVE_INBOX}${qs.stringify(params)}`);
}

export function getTags({ pageNumber = ONE, itemsPerPage = TEN, sort = '-id', filter }) {
  const params = {
    items_per_page: itemsPerPage,
    page: pageNumber,
    sort,
    filter: JSON.stringify(filter),
  };
  return axios.get(`${path.TAGS}${qs.stringify(params)}`);
}

export function getTag({ tagId }) {
  const params = {
    tag_id: tagId,
  };
  return axios.get(path.TAG, { params });
}

export function getBackgroundEvents({ pageNumber = ONE, itemsPerPage = TEN, filterValue = '' }) {
  return axios.get(path.BACKGROUND_EVENTS, {
    params: {
      items_per_page: itemsPerPage,
      page: pageNumber,
      filter: JSON.stringify([{ fieldName: 'type', type: 'Equals', value: filterValue }]),
    },
  });
}

export function getScheduledMessages({ jobId, page = ONE, itemsPerPage = FIVE }) {
  const params = {
    job_id: jobId,
    page,
    items_per_page: itemsPerPage,
    sort: '-sendAt',
  };
  return axios.get(path.SCHEDULED_MESSAGES, { params });
}

export function getCandidateScheduledMessages({ candidateId, page = ONE, itemsPerPage = FIVE }) {
  const params = {
    candidate_id: candidateId,
    page,
    items_per_page: itemsPerPage,
    sort: '-sendAt',
  };

  return axios.get(path.SCHEDULED_MESSAGES, { params });
}

export function updateProfileApi({
  fname,
  lname,
  contactPhoneNumber,
  timezone,
  automaticTextMsg,
  callForwardingNumber,
  isCallForwarding,
  isDisableVoicemail,
  isDuplicateMessageBlocking,
  voicemailMessage,
  unreadMessageNotifications,
  optedOutMessage,
  sendInitialOptOut,
}) {
  const params = {
    first_name: fname,
    last_name: lname,
    phone: contactPhoneNumber,
    timezone,
    duplicate_send_check_flag: isDuplicateMessageBlocking,
    voicemail_text: voicemailMessage,
    voicemail_disabled: isDisableVoicemail,
    auto_response: automaticTextMsg,
    forwarding_enabled: isCallForwarding,
    forwarding_number: callForwardingNumber,
    unread_message_notification_type: unreadMessageNotifications,
    initial_opt_out_msg: optedOutMessage,
    initial_opt_out_enabled: sendInitialOptOut,
  };
  return axios.get(path.UPDATE_USER, { params });
}

export function getCampaignCandidates({
  jobId,
  page = ONE,
  itemsPerPage = FIVE,
  sort,
  searchValue = '',
}) {
  const params = {
    job_id: jobId,
    page,
    items_per_page: itemsPerPage,
    sort,
    filter: JSON.stringify([{ fieldName: 'fullName', type: 'Like', value: searchValue }]),
  };
  return axios.get(path.CAMPAIGN_CANDIDATES, { params, forceUpdate: true });
}

export function getCampaignBGEvent({
  customerId,
  campaignId,
  eventStatus,
  page = ONE,
  itemsPerPage = FIVE,
  sort,
}) {
  const params = {
    account_id: customerId,
    page,
    items_per_page: itemsPerPage,
    sort,
    filter: JSON.stringify([
      { fieldName: 'campaignId', type: 'Equals', value: campaignId },
      ...(eventStatus ? [{ fieldName: 'status', type: 'Equals', value: eventStatus }] : []),
    ]),
  };
  return axios.get(path.BACKGROUND_EVENTS, { params, forceUpdate: true });
}

export function candidateQueryWithTags({ lastContacted, tagsData }) {
  const candidateQuery = {
    lastContacted,
    and: [
      {
        filter: { sqlField: 'status', type: 'Equals', value: 'active' },
      },
    ],
  };
  tagsData.forEach((value) =>
    candidateQuery.and.push({
      filter: { sqlField: 'fullTags.name', type: 'Equals', value: value.name },
    }),
  );
  const params = {
    candidate_query: candidateQuery,
  };
  return axios.get(path.CANDIDATE_QUERY, { params });
}
export function uploadCandidates({ selectedFile, uid }) {
  const params = {
    uid,
  };

  const uploadData = new FormData();
  uploadData.append('file', selectedFile);
  return axios.post(`${path.CANDIDATE_FILE_UPLOAD}${qs.stringify(params)}`, uploadData);
}

export function candidatesFileUpload({ uploadHeaders, customerId, campaignId, uid, isUserUpload }) {
  /* TODO: to refactor the below logic and move it to component. */
  const formattedHeaders = (headers) => {
    const newHeaders = [];
    // eslint-disable-next-line array-callback-return
    headers.map(({ name, index }) => {
      newHeaders.push({ name: name.replace(/\s/g, ''), index });
    });
    return newHeaders;
  };

  const params = {
    account_id: customerId,
    uid,
  };

  const data = {
    uid,
    account_id: customerId,
    fields: JSON.stringify(formattedHeaders(uploadHeaders)),
    client_ver: config.clientVersion,
    app_name: APP_NAME,
    product: productTypes.TEXT_RECRUIT,
  };

  if (campaignId) {
    data.job_id = campaignId;
  }

  const importFilePath = isUserUpload ? path.MGR_USER_IMPORT : path.CANDIDATE_IMPORT_FILE;

  return formAxios.post(`${importFilePath}${qs.stringify(params)}`, qs.stringify(data));
}

export function updateUserDisableAPI({ userId, isDisable }) {
  if (isDisable === false) {
    const params = {
      id: userId,
      status: statusTypes.DISABLED,
    };
    return axios.post(`${path.MGR_USER_DISABLE}${qs.stringify(params)}`);
  }
  const statusType = statusTypes.ACTIVE;
  const params = {
    id: userId,
    status: statusType,
  };
  return axios.post(`${path.MGR_USER_ENABLE}${qs.stringify(params)}`);
}

export const updateUserDeleteAPI = ({ userId, role, firstName, lastName }) => {
  const params = {
    id: userId,
    role,
  };
  return axios
    .post(`${path.MGR_USER_REVOKE}&id=${params.id}&role=${params.role}`)
    .then((response) => {
      response.firstName = firstName;
      response.lastName = lastName;
      return response;
    });
};

export function updateRoleAPI({ userId, firstName, lastName, role = 'User' }) {
  const params = {
    id: userId,
    role,
    first_name: firstName,
    last_name: lastName,
  };
  return axios.post(`${path.MGR_USER_UPDATE}${qs.stringify(params)}`);
}

export function cancelScheduledMessages({ id, customerId }) {
  const params = {
    id,
    account_id: customerId,
  };

  const data = {
    id,
    client_ver: config.clientVersion,
    app_name: APP_NAME,
    product: productTypes.TEXT_RECRUIT,
    account_id: customerId,
  };

  return formAxios.post(`${path.CANCEL_MESSAGE}${qs.stringify(params)}`, qs.stringify(data));
}

export function removeMultipleSelectedApi({ candidateIds, jobId, customerId }) {
  const params = {
    id: jobId,
    account_id: customerId,
  };

  const data = {
    candidate_ids: candidateIds.join(),
    job_id: jobId,
    app_name: APP_NAME,
    product: productTypes.TEXT_RECRUIT,
    client_ver: config.clientVersion,
    account_id: customerId,
  };
  return formAxios.post(
    `${path.REMOVE_MULTICANDIDATE_FROM_CAMPAIGN}${qs.stringify(params)}`,
    qs.stringify(data),
  );
}

export function fetchResetPasswordApi({ userName }) {
  const params = {
    username: userName,
  };
  return axios.post(`${path.FORGOT_PASSWORD}${qs.stringify(params)}`);
}
